import { useEventListener } from '@vueuse/core';
import { onUnmounted, ref, unref, watch } from 'vue';

export const DEFAULT_POLLING_INTERVAL = 1000;
export const DEFAULT_REFETCH_ON_WINDOW_FOCUS = true;
export const DEFAULT_RETRY_COUNT = 100;

/**
 * Polling options
 * @typedef {Object} PollingOptions
 * @param {Function} queryFn - Function that is triggered to make the request.
 * @param {boolean} [n=false] enabled - If polling enabled.
 * @param {number} [n=DEFAULT_POLLING_INTERVAL] pollingInterval - Miliseconds between each polling.
 * @param {number} [n=DEFAULT_REFETCH_ON_WINDOW_FOCUS] refetchOnWindowFocus - If should refetch immediately when window
 * focused.
 * @param {number} [n=DEFAULT_RETRY_COUNT] retryCount - Tries before error thrown when not successfull.
 * @return {PollingReturn} - Reactive state and data.
 */

/**
 * Polling return
 * @typedef {Object} PollingReturn
 * @property {boolean} loaded - Reactive state, false until the first poll is finished.
 * @property {boolean} fetching - Reactive state, true when polling.
 * @property {boolean} failed - Reactive state, when error occurs.
 * @property {string} error - Error message when error occurs.
 * @property {*} data - Result data of the polling.
 */

/**
 * Composable used for polling remote state
 * Stops when tab blurred, starts again when focused (unless disabled by param).
 *
 * @param {PollingOptions} options - All options the composable takes.
 */
function polling({
  enabled = ref(true),
  pollingInterval = ref(DEFAULT_POLLING_INTERVAL),
  refetchOnWindowFocus = ref(DEFAULT_REFETCH_ON_WINDOW_FOCUS),
  retryCount = ref(DEFAULT_RETRY_COUNT),

  queryFn,
}) {
  const loaded = ref(false);
  const fetching = ref(false);
  const failed = ref(false);
  const error = ref(null);
  const data = ref(null);

  let timeoutId = null;
  let abortController = null;
  let currentRetries = 0;

  const clearPolling = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  const executePoll = async () => {
    timeoutId = setTimeout(executePoll, unref(pollingInterval));

    if (fetching.value || !unref(enabled)) return;

    try {
      abortController = new AbortController();
      fetching.value = true;

      const newData = await queryFn(null, { abortController });
      if (!newData) throw new Error('Failed to fetch data');

      data.value = newData;
      failed.value = false;
      error.value = null;

      currentRetries = 0;
    } catch (err) {
      if (err.name === 'AbortError') return;

      failed.value = true;
      error.value = err;

      if (currentRetries < unref(retryCount)) {
        currentRetries++;
        return;
      }

      clearTimeout(timeoutId);
    } finally {
      loaded.value = true;
      fetching.value = false;
    }
  };

  watch(
    enabled,
    (newValue) => {
      clearPolling();

      if (newValue) {
        executePoll();
      }
    },
    { immediate: true },
  );

  useEventListener(document, 'visibilitychange', () => {
    if (!unref(refetchOnWindowFocus) || !unref(enabled)) return;

    if (document.visibilityState === 'hidden') {
      clearPolling();
    } else if (document.visibilityState === 'visible') {
      executePoll();
    }
  });

  onUnmounted(() => {
    clearPolling();
  });

  return {
    loaded,
    fetching,
    failed,
    error,
    data,
  };
}

export default polling;
